/**
 * Relatos crypto core — NIP-44 v2 encryption wrappers.
 *
 * Two modes:
 * - boardEncrypt/boardDecrypt: symmetric board encryption. The 32-byte epoch key is
 *   used directly as a NIP-44 v2 conversation key (it is uniformly random CSPRNG
 *   output, so it satisfies the conversation-key entropy requirement).
 * - peerEncrypt/peerDecrypt: ECDH conversation key between two Nostr keys, used for
 *   invite payloads and per-member key distribution.
 *
 * Any decryption failure throws — callers (UI) must catch and render the raw
 * ciphertext as scrambled text, never an empty state (CLAUDE.md, metadata rule).
 */
import { nip44, getPublicKey } from 'nostr-tools';
import { hexToBytes } from '@noble/hashes/utils.js';

/** NIP-44 hard limit: plaintext MUST NOT exceed 65,535 bytes. Enforced at every
 *  encryption entry point so an oversized post fails with a clear error instead
 *  of a buried library exception. */
export const NIP44_MAX_PLAINTEXT = 65535;

function checkSize(plain: string): void {
  if (new TextEncoder().encode(plain).length > NIP44_MAX_PLAINTEXT) {
    throw new Error(`Payload exceeds NIP-44 limit (${NIP44_MAX_PLAINTEXT} bytes)`);
  }
}

export function boardEncrypt(epochKeyHex: string, payload: unknown): string {
  const key = hexToBytes(epochKeyHex);
  const plain = JSON.stringify(payload);
  checkSize(plain);
  return nip44.v2.encrypt(plain, key);
}

export function boardDecrypt<T = unknown>(epochKeyHex: string, ct: string): T {
  const key = hexToBytes(epochKeyHex);
  const plain = nip44.v2.decrypt(ct, key);
  return JSON.parse(plain) as T;
}

/** True if `ct` decrypts under `epochKeyHex` — cheap membership probe without parsing. */
export function boardCanDecrypt(epochKeyHex: string, ct: string): boolean {
  try {
    nip44.v2.decrypt(ct, hexToBytes(epochKeyHex));
    return true;
  } catch {
    return false;
  }
}

export function peerEncrypt(senderSkHex: string, recipientPkHex: string, payload: unknown): string {
  const ck = nip44.v2.utils.getConversationKey(hexToBytes(senderSkHex), recipientPkHex);
  const plain = JSON.stringify(payload);
  checkSize(plain);
  return nip44.v2.encrypt(plain, ck);
}

export function peerDecrypt<T = unknown>(recipientSkHex: string, senderPkHex: string, ct: string): T {
  const ck = nip44.v2.utils.getConversationKey(hexToBytes(recipientSkHex), senderPkHex);
  return JSON.parse(nip44.v2.decrypt(ct, ck)) as T;
}

export function selfPubkey(skHex: string): string {
  return getPublicKey(hexToBytes(skHex));
}
